import * as THREE from "three";
import { RoomEnvironment } from "three/addons/environments/RoomEnvironment.js";
import { GradientEnvironment } from "../../utils/GradientEnvironment.js";
import { SHOW_DEV_TOOLS } from "../../config.js";

// Sky-mode IBL is set per furniture material rather than through scene.environment.
const SKY_ENV_MAP_INTENSITY = 0.6;

// Dev-only IBL A/B settings. Deliberately kept out of StateManager: they're an
// evaluation tool, so they must not leak into share links or preset layouts.
const IBL_DEV_STORAGE_KEY = "threejs_ibl_dev";

// Image-based lighting (sky PMREM and the dev baked IBLs), installed on SceneManager.prototype.
export const iblMethods = {
  // Bake the active IBL onto scene.environment, where every PBR material picks it up.
  bakeIbl() {
    if (!this.pmremGenerator || !this.scene) return;

    const environment = this.iblMode === "room"
      ? new RoomEnvironment()
      : new GradientEnvironment(this.iblGradient);
    const renderTarget = this.pmremGenerator.fromScene(environment, 0.04);
    environment.dispose();

    this._setEnvTarget(renderTarget);

    this.scene.environment = this.envMap;
    this.scene.environmentIntensity = this.environmentIntensity;
    // RoomEnvironment's lights were placed for a viewer on -X; the gradient is symmetric about Y.
    this.scene.environmentRotation.set(0, this.iblMode === "room" ? Math.PI / 2 : 0, 0);
  },

  // Sky and baked IBL share one owned PMREM target, so every rebake frees the one it replaces.
  _setEnvTarget(renderTarget) {
    this._envTarget?.dispose();
    this._envTarget = renderTarget;
    this.envMap = renderTarget?.texture ?? null;
  },

  /** Put the realtime background and IBL back on the scene, for whoever borrowed them (the path tracer). */
  applyRealtimeEnvironment() {
    if (!this.scene) return;
    const background = this.environmentManager?.getBackdropColor();
    if (background != null) this.scene.background = new THREE.Color(background);
    this.scene.environment = this.iblMode === "sky" ? null : this.envMap;
    this.scene.environmentIntensity = this.environmentIntensity;
  },

  // Switch between the sky bake and the two baked IBLs ("sky" | "gradient" | "room").
  setIblMode(mode) {
    if (!this.scene) return;
    this.iblMode = ["sky", "room", "gradient"].includes(mode) ? mode : "gradient";

    if (this.iblMode === "sky") {
      this.scene.environment = null;
      this._setEnvTarget(null);
      this.lastEnvUpdateTime = null;
      this.updateEnvironmentMap();
    } else {
      // Explicit material.envMap wins over scene.environment, so drop the sky map first.
      this.clearMaterialEnvMaps();
      this.bakeIbl();
    }

    this.requestShadowUpdate();
  },

  // Retune the gradient's nadir/zenith luminance or falloff and re-bake.
  setIblGradient(values) {
    this._mergeIblGradient(values);
    this._scheduleIblRebake();
  },

  _mergeIblGradient(values) {
    Object.entries(values || {}).forEach(([key, value]) => {
      if (key in this.iblGradient && Number.isFinite(value)) {
        this.iblGradient[key] = value;
      }
    });
  },

  // Dragging a slider fires many events per frame; one PMREM bake per frame is plenty.
  _scheduleIblRebake() {
    if (this.iblMode === "sky" || this._iblRebakeHandle) return;
    this._iblRebakeHandle = requestAnimationFrame(() => {
      this._iblRebakeHandle = null;
      this.bakeIbl();
      this.requestShadowUpdate();
    });
  },

  clearMaterialEnvMaps() {
    this.scene.traverse((object) => {
      if (!object.isMesh || !object.material || !object.userData.isFurniture) return;
      const materials = Array.isArray(object.material) ? object.material : [object.material];
      materials.forEach((mat) => {
        if (!mat.envMap) return;
        mat.envMap = null;
        mat.needsUpdate = true;
      });
    });
  },

  // Live tweak for the IBL contribution (scene.environmentIntensity).
  setEnvironmentIntensity(intensity) {
    if (!Number.isFinite(intensity)) return;
    this.environmentIntensity = intensity;
    if (this.scene) this.scene.environmentIntensity = intensity;
  },

  loadEnvironmentSettings() {
    try {
      const saved = JSON.parse(localStorage.getItem(IBL_DEV_STORAGE_KEY) || "null");
      if (!saved) return;
      if (["sky", "gradient", "room"].includes(saved.mode)) this.iblMode = saved.mode;
      if (Number.isFinite(saved.intensity)) this.environmentIntensity = saved.intensity;
      this._mergeIblGradient(saved.gradient);
    } catch {
      // Corrupt or unavailable storage — fall back to the defaults above.
    }
  },

  saveEnvironmentSettings() {
    if (!SHOW_DEV_TOOLS) return;
    try {
      localStorage.setItem(IBL_DEV_STORAGE_KEY, JSON.stringify({
        mode: this.iblMode,
        intensity: this.environmentIntensity,
        gradient: this.iblGradient,
      }));
    } catch {
      // Storage full / disabled — the setting just won't survive the refresh.
    }
  },

  updateEnvironmentMap() {
    // A baked IBL is static and already on scene.environment — nothing to rebake.
    if (this.iblMode !== "sky") return;
    if (!this.pmremGenerator || !this.scene || !this.skybox) return;

    try {
      const lensflareWasVisible = this.skybox.lensflare ? this.skybox.lensflare.visible : false;
      if (this.skybox.lensflare) {
        this.skybox.lensflare.visible = false;
      }

      const objectsToHide = [];
      this.scene.traverse((object) => {
        if (object.userData.isFurniture || object.userData.isFloor ||
            object.userData.isWall || object.userData.isCeiling) {
          if (object.visible) {
            objectsToHide.push(object);
            object.visible = false;
          }
        }
      });

      this._setEnvTarget(this.pmremGenerator.fromScene(this.scene));

      objectsToHide.forEach(obj => obj.visible = true);

      if (this.skybox.lensflare) {
        this.skybox.lensflare.visible = lensflareWasVisible;
      }

      this.applyEnvironmentMap();
    } catch (error) {
      console.warn('Failed to generate environment map:', error);
    }
  },

  applyEnvironmentMap() {
    if (!this.envMap) return;

    this.scene.traverse((object) => {
      if (!object.isMesh || !object.material || !object.userData.isFurniture) return;
      const materials = Array.isArray(object.material) ? object.material : [object.material];
      for (const mat of materials) {
        mat.envMap = this.envMap;
        mat.envMapIntensity = SKY_ENV_MAP_INTENSITY;
        mat.needsUpdate = true;
      }
    });
  },
};
